import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from 'react-router-dom';
import { generateLocalizedUrl } from "@/lib/slugUtils";
import { supportedLanguages, type SupportedLanguage } from "@/lib/i18n";
import OptimizedImage from "@/components/OptimizedImage";

const AboutSection = () => {
  const { t } = useTranslation('about');
  const navigate = useNavigate();
  const { lang } = useParams<{ lang: SupportedLanguage }>();
  const currentLang = lang && supportedLanguages.includes(lang) ? lang : 'en';

  const handleContactClick = () => {
    const contactUrl = generateLocalizedUrl('contact', currentLang);
    navigate(contactUrl);
  };
  
  return (
    <section id="about" className="py-20 bg-gradient-subtle">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <Badge variant="outline" className="mb-4">{t('badge')}</Badge>
          <h2 className="text-4xl font-bold mb-6">{t('title')}</h2>
        </div>
        
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-8 sm:gap-12 items-center">
          <div>
            <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
              {t('description')}
            </p>
            
            <div className="grid grid-cols-2 gap-3 sm:gap-6 mb-8">
              <div className="text-center p-3 sm:p-4 bg-card rounded-lg border">
                <div className="text-xl sm:text-2xl font-bold text-primary mb-1">{t('stats.experience.value')}</div>
                <div className="text-xs sm:text-sm text-muted-foreground">{t('stats.experience.label')}</div>
              </div>
              <div className="text-center p-3 sm:p-4 bg-card rounded-lg border">
                <div className="text-xl sm:text-2xl font-bold text-primary mb-1">{t('stats.projects.value')}</div>
                <div className="text-xs sm:text-sm text-muted-foreground">{t('stats.projects.label')}</div>
              </div>
              <div className="text-center p-3 sm:p-4 bg-card rounded-lg border">
                <div className="text-xl sm:text-2xl font-bold text-primary mb-1">{t('stats.satisfaction.value')}</div>
                <div className="text-xs sm:text-sm text-muted-foreground">{t('stats.satisfaction.label')}</div>
              </div>
              <div className="text-center p-3 sm:p-4 bg-card rounded-lg border">
                <div className="text-xl sm:text-2xl font-bold text-primary mb-1">{t('stats.support.value')}</div>
                <div className="text-xs sm:text-sm text-muted-foreground">{t('stats.support.label')}</div>
              </div>
            </div>
            
            <Button variant="outline" size="lg" onClick={handleContactClick}>
              {t('button')}
            </Button>
          </div>
          
          <div className="flex justify-center mt-8 lg:mt-0">
            <div className="relative">
              <OptimizedImage 
                src="/images/daniel.png"
                placeholder="https://images.unsplash.com/photo-1581090464777-f3220bbe1b8b?w=64&h=64&q=10"
                alt={t('imageAlt')}
                className="w-64 h-64 sm:w-80 sm:h-80 rounded-2xl object-cover object-center shadow-elegant hover:scale-105 transition-elegant"
              />
              <div className="absolute -bottom-3 -right-3 sm:-bottom-4 sm:-right-4 bg-accent text-accent-foreground rounded-xl p-3 sm:p-4 shadow-lg animate-float">
                <div className="text-base sm:text-lg font-bold">{t('stats.satisfaction.value')}</div>
                <div className="text-xs">{t('badgeText')}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;